import { useId, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { compute } from "../lib/loan";

function OutputField({
  label,
  value,
  testid,
  emphasis,
}: {
  label: string;
  value: string;
  testid: string;
  emphasis?: boolean;
}) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        readOnly
        data-testid={testid}
        value={value}
        className={`font-mono ${emphasis ? "text-xl font-bold" : "text-lg"}`}
      />
    </div>
  );
}

export function LoanTool() {
  const uid = useId();
  const [principal, setPrincipal] = useState("20000");
  const [rate, setRate] = useState("6");
  const [years, setYears] = useState("5");

  const result = useMemo(
    () =>
      compute(Number(principal), Number(rate), Math.round(Number(years) * 12)),
    [principal, rate, years],
  );

  return (
    <div className="space-y-6">
      <div className="grid gap-4 rounded-2xl border border-border bg-card/60 p-6 sm:grid-cols-3 sm:p-8">
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-amt`}>{m.ln_principal_label()}</Label>
          <Input
            id={`${uid}-amt`}
            type="text"
            inputMode="decimal"
            value={principal}
            onChange={(e) => setPrincipal(e.target.value)}
            placeholder={m.ln_principal_ph()}
            autoComplete="off"
            className="font-mono text-lg"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-rate`}>{m.ln_rate_label()}</Label>
          <Input
            id={`${uid}-rate`}
            type="text"
            inputMode="decimal"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            autoComplete="off"
            className="font-mono text-lg"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-term`}>{m.ln_term_label()}</Label>
          <Input
            id={`${uid}-term`}
            type="text"
            inputMode="numeric"
            value={years}
            onChange={(e) => setYears(e.target.value)}
            autoComplete="off"
            className="font-mono text-lg"
          />
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        {result ? (
          <div className="grid gap-4 sm:grid-cols-3">
            <OutputField
              label={m.ln_monthly_out()}
              value={result.monthlyPayment.toFixed(2)}
              testid="ln-monthly"
              emphasis
            />
            <OutputField
              label={m.ln_total_out()}
              value={result.totalPaid.toFixed(2)}
              testid="ln-total"
            />
            <OutputField
              label={m.ln_interest_out()}
              value={result.totalInterest.toFixed(2)}
              testid="ln-interest"
            />
          </div>
        ) : (
          <p data-testid="ln-error" className="text-sm text-muted-foreground">
            {m.ln_invalid()}
          </p>
        )}
      </div>
    </div>
  );
}
